class Command {
  validate(): boolean {
    return true;
  }
}

class Query {
  ///https://developer.mozilla.org/pt-BR/docs/Web/HTTP/Content_negotiation
  ///https://developer.mozilla.org/pt-BR/docs/Web/HTTP/Headers/Accept
  public readonly accept: string;
  public readonly forceRefresh: boolean;
  public readonly params: Record<string, string>;

  /// * [params] Normalmente utilizado para os parâmetros a serem enviados na URL,
  /// tais como identificadores de recursos (ex.: contaId, enderecoId). É possível
  /// também utilizá-lo para passagem de parâmetros para outros fins.
  /// Os valores deverão ser recuperados e validados antes do seu uso.
  /// * [accept] Por default, a API vai retornar os recursos no
  /// formato __application/json__.
  /// No entanto é possível definir tipos de mídia personalizados
  /// que incluem informações, permitindo que o aplicativo cliente
  /// indique qual conteúdo de resposta está esperando.
  constructor(options: {
    params?: Record<string, string>;
    accept?: string;
    forceRefresh?: boolean;
  } = {}) {
    if (options.accept === '') {
      throw new Error('Parâmetro inválido');
    }

    this.accept = options.accept ?? 'application/json';
    this.forceRefresh = options.forceRefresh ?? false;
    this.params = options.params ?? {};
  }

  static initial(): Query {
    return new Query();
  }

  get(key: string): string | undefined {
    return this.params[key];
  }

  set(key: string, value: string): void {
    this.params[key] = value;
  }

  clear(): void {
    Object.keys(this.params).forEach(key => delete this.params[key]);
  }
}

class QueryFilter extends Query {
  public readonly queryParams: Record<string, string>;

  /// * [sort] deve conter os nomes dos atributos que serão usados
  /// para ordenar a lista, __separados por vírgula__.
  /// * [desc] por default a ordem é ascendente.
  /// Para uma ordenação descendente, é necessário adicionar
  /// esse parâmetro (__sem nenhum valor__).
  /// Em alguns casos específicos pode-se querer especificar
  /// quais atributos serão ascendentes e quais serão descendentes.
  /// Nesse caso, o parâmetro [desc] deve conter a lista dos atributos
  /// que serão descendentes, ficando os demais ascendentes por default.
  /// * [params] Normalmente utilizado para os parâmetros a serem enviados na URL,
  /// tais como identificadores de recursos (ex.: contaId, enderecoId). É possível
  /// também utilizá-lo para passagem de parâmetros para outros fins.
  /// Os valores deverão ser recuperados e validados antes do seu uso.
  /// * [accept] Por default, a API vai retornar os recursos no
  /// formato __application/json__.
  /// No entanto é possível definir tipos de mídia personalizados
  /// que incluem informações, permitindo que o aplicativo cliente
  /// indique qual conteúdo de resposta está esperando.
  constructor(options: {
    sort?: string;
    desc?: string;
    params?: Record<string, string>;
    accept?: string;
    forceRefresh?: boolean;
    queryParams?: Record<string, string>;
  } = {}) {
    super({
      params: options.params,
      accept: options.accept,
      forceRefresh: options.forceRefresh,
    });
    this.queryParams = QueryFilter._sort(options.sort, options.desc, options.queryParams);
  }

  static initial(): QueryFilter {
    return new QueryFilter();
  }

  private static _sort(
    sort?: string,
    desc?: string,
    params?: Record<string, string>,
  ): Record<string, string> {
    const result: Record<string, string> = {};
    
    if (params) {
      delete params['sort'];
      delete params['desc'];
    }

    if (sort && sort.length > 0) {
      result['sort'] = sort;
    }
    if (desc) {
      result['desc'] = desc;
    }

    if (params) {
      Object.assign(result, params);
    }
    return result;
  }

  copyWith(options: {
    forceRefresh?: boolean;
    params?: Record<string, string>;
    queryParams?: Record<string, string>;
  }): QueryFilter {
    return new QueryFilter({
      params: options.params ?? this.params,
      forceRefresh: options.forceRefresh ?? this.forceRefresh,
      queryParams: options.queryParams ?? this.queryParams,
    });
  }

  get(key: string): string | undefined {
    return this.queryParams[key];
  }

  set(key: string, value: string): void {
    this.queryParams[key] = value;
  }

  url(key: string, value: string): void {
    this.params[key] = value;
  }

  clear(): void {
    Object.keys(this.queryParams).forEach(key => delete this.queryParams[key]);
  }
}

class QueryPaginated extends QueryFilter {
  /// A paginação é uma peça muito importante,
  /// e faz sentido que ela apareça na URL, para clareza e simplicidade.
  /// Propomos a utilização de uma faixa de valores, sobre o índice da sua coleção.
  ///
  /// __Exemplo:__
  /// Recursos do índice 100 até o 150 inclusive, equivalem a:
  /// [offset]=100 [limit]=50.
  /// * [sort] deve conter os nomes dos atributos que serão usados
  /// para ordenar a lista, __separados por vírgula__.
  /// * [desc] por default a ordem é ascendente.
  /// Para uma ordenação descendente, é necessário adicionar
  /// esse parâmetro (__sem nenhum valor__).
  /// Em alguns casos específicos pode-se querer especificar
  /// quais atributos serão ascendentes e quais serão descendentes.
  /// Nesse caso, o parâmetro [desc] deve conter a lista dos atributos
  /// que serão descendentes, ficando os demais ascendentes por default.
  /// * [params] Normalmente utilizado para os parâmetros a serem enviados na URL,
  /// tais como identificadores de recursos (ex.: contaId, enderecoId). É possível
  /// também utilizá-lo para passagem de parâmetros para outros fins.
  /// Os valores deverão ser recuperados e validados antes do seu uso.
  /// * [accept] Por default, a API vai retornar os recursos no
  /// formato __application/json__.
  /// No entanto é possível definir tipos de mídia personalizados
  /// que incluem informações, permitindo que o aplicativo cliente
  /// indique qual conteúdo de resposta está esperando.
  constructor(
    offset: number,
    limit: number,
    options: {
      sort?: string;
      desc?: string;
      params?: Record<string, string>;
      accept?: string;
      forceRefresh?: boolean;
      queryParams?: Record<string, string>;
    } = {}
  ) {
    super({
      sort: options.sort,
      desc: options.desc,
      params: options.params,
      accept: options.accept,
      forceRefresh: options.forceRefresh,
      queryParams: QueryPaginated._range(offset, limit, options.queryParams),
    });
  }

  static initial(): QueryPaginated {
    return new QueryPaginated(0, 0);
  }

  private static _range(
    offset: number,
    limit: number,
    params?: Record<string, string>,
  ): Record<string, string> {
    const result: Record<string, string> = { 'range': `${offset}-${limit}` };
    
    if (params) {
      delete params['range'];
      Object.assign(result, params);
    }
    return result;
  }
}

export {
  Command,
  Query,
  QueryFilter,
  QueryPaginated
};
